<template>
  <div>
    <app-header />

    <div class="gantt-nav project-nav container-fluid">
      <div class="row">
        <div class="col-sm-4">
          <h2 class="d-inline-block pe-3">Project Planner</h2>
          {{ rangeStart | moment('MMMM YYYY') }} - {{ rangeEnd | moment('MMMM YYYY') }}
        </div>
        <div class="col-sm-4 text-center">
          <div role="group" aria-label="Basic example" class="btn-group">
            <button @click="filter = 'all'" type="button" class="btn btn-primary" :class="{ active: filter === 'all' }">
              All
            </button>
            <button
              @click="filter = 'projects'"
              type="button"
              class="btn btn-primary"
              :class="{ active: filter === 'projects' }"
            >
              Projects
            </button>
            <button
              @click="filter = 'future'"
              type="button"
              class="btn btn-primary"
              :class="{ active: filter === 'future' }"
            >
              Potential Projects
            </button>
          </div>
        </div>
        <div class="col-sm-4">
          <ul class="nav nav-pills float-end">
            <li @click="undo()">
              <button type="button" class="btn">
                <i class="far fa-undo"></i>
              </button>
            </li>
            <li @click="redo()">
              <button type="button" class="btn">
                <i class="far fa-redo"></i>
              </button>
            </li>
            <li>
              <button type="button" class="btn" v-c-modal:filterOptionsModal="{}">
                <i class="far fa-filter"></i>
              </button>
            </li>
            <li></li>
          </ul>
        </div>
      </div>
    </div>
    <gantt
      class="gantt-container"
      :tasks="ganttTasks"
      :start="rangeStart"
      :end="rangeEnd"
      @task-added="taskAdded"
      @task-changing="taskChanging"
      @task-updated="taskUpdated"
      @task-deleted="taskDeleted"
      @task-selected="taskSelected"
      @task-edit="editTask"
    ></gantt>

    <filter-options-drawer ref="filterOptionsModal"></filter-options-drawer>
    <gantt-item-form @select-task="selectTask" ref="ganttItemFormModal"></gantt-item-form>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { cloneDeep, findIndex, filter } from 'lodash'
import Gantt from '../Gantt.vue'
import FilterOptionsDrawer from './FilterOptionsDrawer'
import GanttItemForm from './GanttItemForm'
import $ from 'jquery'

export default {
  components: {
    Gantt,
    FilterOptionsDrawer,
    GanttItemForm,
  },
  data() {
    return {
      tasks: [],
      loading: false,
      taskBeforeLastUpdate: null,
      filter: 'all',
    }
  },
  mounted() {
    this.$bus.$on('menu-toggled', function(id) {
      gantt.setSizes()
    })

    this.getGanttItems({
      start: this.rangeStart.format('YYYY/MM/DD'),
      end: this.rangeEnd.format('YYYY/MM/DD'),
      filter: this.filter,
    })
  },

  watch: {
    filter() {
      this.getGanttItems({
        start: this.rangeStart.format('YYYY/MM/DD'),
        end: this.rangeEnd.format('YYYY/MM/DD'),
        filter: this.filter,
      })
    },
  },

  computed: {
    ...mapGetters('projectsPlanner', ['ganttItemsSanitized']),
    ...mapState('projectsPlanner', ['filters']),

    rangeStart() {
      return moment().subtract(3, 'weeks')
    },

    rangeEnd() {
      return moment().add(18, 'months')
    },

    ganttTasks() {
      return filter(this.ganttItemsSanitized, item => {
        if (!item.stage_id) {
          return true
        }

        if (!this.filters.showUnconfirmed && !parseInt(item.confirmed[0])) {
          return false
        }

        if (!this.filters.stages1 && item.stage_id == 1) {
          return false
        }

        if (!this.filters.stages2 && item.stage_id == 2) {
          return false
        }

        if (!this.filters.stages3 && item.stage_id == 3) {
          return false
        }

        if (this.filters.clientId && (!item.client_id || item.client_id !== this.filters.clientId)) {
          return false
        }

        return true
      })
    },
  },
  methods: {
    ...mapActions('projectsPlanner', [
      'getGanttItems',
      'addGanttItem',
      'updateProjectGanttItem',
      'updateGanttItem',
      'deleteGanttItem',
      'setGanttItem',
      'resetGanttItem',
    ]),

    undo() {
      gantt.undo()
    },

    redo() {
      gantt.redo()
    },

    taskSelected(task) {},

    selectTask(id) {
      gantt.selectTask(id)
    },

    taskChanging(id, mode, task) {
      //stamping the task before any operation for the purpose of rolling back the change
      this.taskBeforeLastUpdate = task
    },

    taskAdded(task) {
      //reset state for add form
      this.resetGanttItem()
      this.$refs.ganttItemFormModal.open()
    },
    taskUpdated(id, task) {
      if (task.project_stage) {
        let payload = {
          commenced: moment(task.start_date).format('YYYY-MM-DD'),
          completed: moment(task.end_date).format('YYYY-MM-DD'),
        }

        this.updateProjectGanttItem({ id: id, payload: payload })
      } else {
        let payload = {
          name: task.text,
          start_date: moment(task.start_date).format('YYYY-MM-DD'),
          end_date: moment(task.end_date).format('YYYY-MM-DD'),
          order: task.order,
          progress: task.progress,
          color: task.color,
          stage_id: parseInt(task.stage_id),
          confirmed: task.confirmed.length ? parseInt(task.confirmed[0]) : 0,
        }

        this.updateGanttItem({ id: id.replace('G', ''), payload: payload })
      }
    },
    taskDeleted(id, task) {
      if (!task.project_stage) {
        this.deleteGanttItem(id)
      }
    },
    logLinkUpdate(id, mode, link) {
      let message = `Link ${mode}: ${id}`
      if (link) {
        message += ` ( source: ${link.source}, target: ${link.target} )`
      }
    },

    editTask(id, task) {
      this.setGanttItem({
        id: task.id,
        name: task.text,
        start_date: moment(task.start_date).format('YYYY-MM-DD'),
        end_date: moment(task.end_date).format('YYYY-MM-DD'),
        order: task.order,
        progress: task.progress,
        color: task.color,
        stage_id: parseInt(task.stage_id),
        client_id: parseInt(task.client_id),
        confirmed: task.confirmed.length ? parseInt(task.confirmed[0]) : 0,
        workdays: task.duration,
        budget_id: task.budget_id,
        budget_approval_id: task.budget_approval_id,
        approved_hours: task.approved_hours,
        project_stage: task.project_stage,
      })

      this.$refs.ganttItemFormModal.open()
    },
  },
}
</script>

<style scoped>
.gantt-nav {
  /* height: 60px; */
  /* margin-left: -15px; */
  /* margin-right: -15px; */
  padding-top: 16px;
}

.gantt-container {
  overflow: hidden;
  position: absolute;
  top: 140px;
  bottom: 0px;
  right: 0;
  left: 0;
}
.gantt-messages {
  list-style-type: none;
  height: 50%;
  margin: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-left: 5px;
}
.gantt-messages > .gantt-message {
  background-color: #f4f4f4;
  box-shadow: inset 5px 0 #d69000;
  font-family: Geneva, Arial, Helvetica, sans-serif;
  font-size: 14px;
  margin: 5px 0;
  padding: 8px 0 8px 10px;
}
.gantt-selected-info {
  border-bottom: 1px solid #cecece;
  box-sizing: border-box;
  font-family: Geneva, Arial, Helvetica, sans-serif;
  height: 50%;
  line-height: 28px;
  padding: 10px;
}
.gantt-selected-info h2 {
  border-bottom: 1px solid #cecece;
}

.select-task-prompt h2 {
  color: #d9d9d9;
}
</style>
